import { formatDate } from "./formatDate";
import { inLateWindow } from "./lateEntries";

// One home-page feed instead of Upcoming / Open / Closed / Results tabs.
//
// The server still decides which listings an event is in (the four
// /feis/get*Events listings); this file merges them into one list, gives
// every event a single display state, and splits the list by the event's days:
//   today  - events running now: from their first day to midnight at the end
//            of their last day (results-first on event day)
//   future - open / late / upcoming / closed, soonest event first
//   past   - results, newest first
// Every item also says whether the event is on the results listing
// (hasResults: at least one result published, Devin 2026-10-31), kept apart
// from where it is placed, so one event can be on the Events tab (running
// now) and on the Results tab (something published) at once.
//
// Dates follow the app's convention: feis/grade/Oireachtas dates are stored
// as UTC midnight of a calendar day and read with formatDate (that calendar
// day at local midnight).

export const FEED_STATES = ["today", "open", "late", "upcoming", "closed", "results"];

// The registration-state listings in precedence order, for an event that
// comes back from more than one (open beats closed beats upcoming). The
// results listing is tracked separately as hasResults.
const STATE_LISTINGS = ["open", "closed", "upcoming"];
const LISTINGS = ["results", ...STATE_LISTINGS];

export const eventDateOf = (event) => {
  if (!event) return null;
  if (event.eventType === "Oireachtas") return event.eventDays?.[0] || event.feis_date || null;
  if (event.eventType === "Grade Exam") return event.exam_date || null;
  return event.feis_date || null;
};

const pad = (n) => String(n).padStart(2, "0");

// A stored calendar day (UTC midnight of that day) as "YYYY-MM-DD", read
// straight from the stored value so it never shifts with the browser's zone.
const storedDayKey = (d) => {
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? null : t.toISOString().slice(0, 10);
};

// The viewer's own calendar day.
const localDayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// The calendar day at `now` in an IANA time zone; null when the zone is
// missing or not understood (the caller falls back to the viewer's day).
const zoneDayKey = (now, timeZone) => {
  if (!timeZone) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone, year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(now);
    const get = (type) => parts.find((p) => p.type === type)?.value;
    const [y, m, d] = [get("year"), get("month"), get("day")];
    return y && m && d ? `${y}-${m}-${d}` : null;
  } catch (e) {
    return null;
  }
};

// "Today" for an event: an Oireachtas uses its own time zone, the same
// midnight the server's closed listing uses (it ends at the end of the last
// day there); feis and grade exams (one calendar day, no zone) use the
// viewer's day.
const todayKeyFor = (event, now) =>
  (event?.eventType === "Oireachtas" && zoneDayKey(now, event.timeZone)) || localDayKey(now);

// Every calendar day the event has stored. An Oireachtas stores its first and
// last day (the admin wizard writes [start, end]); the days between are
// event days too, as the backend's eventDayKeys counts them.
const eventDaysOf = (event) => {
  if (event?.eventType === "Oireachtas" && Array.isArray(event.eventDays) && event.eventDays.length) {
    return event.eventDays;
  }
  const d = eventDateOf(event);
  return d ? [d] : [];
};

// { firstKey, lastKey } of the event's days ("YYYY-MM-DD"), or null.
export const eventSpanKeys = (event) => {
  const keys = eventDaysOf(event).map(storedDayKey).filter(Boolean).sort();
  return keys.length ? { firstKey: keys[0], lastKey: keys[keys.length - 1] } : null;
};

// "before" | "current" | "after" the event's days, or null without days.
export const eventPhase = (event, now = new Date()) => {
  const span = eventSpanKeys(event);
  if (!span) return null;
  const today = todayKeyFor(event, now);
  if (today < span.firstKey) return "before";
  if (today > span.lastKey) return "after";
  return "current";
};

// Running now: on any day from the event's first to its last, the unstored
// middle days of an Oireachtas included (Sat Nov 7 of a Fri-Sun ECRO).
export const isEventToday = (event, now = new Date()) => eventPhase(event, now) === "current";

const bucketState = (bucket, event) => {
  if (bucket === "open") return event?.registrationState === "late" ? "late" : "open";
  return bucket;
};

// Maps a display state to the `from` value the cards, NavigationHelper and
// event pages already understand.
export const fromForState = (state) =>
  state === "today" || state === "results" ? "result"
    : state === "late" ? "open"
      : state;

const time = (d) => (d ? formatDate(d).getTime() : Number.POSITIVE_INFINITY);
const soonest = (a, b) => time(eventDateOf(a.event)) - time(eventDateOf(b.event));
const newestFirst = (a, b) => -soonest(a, b);

/**
 * @param {{open?: object[], upcoming?: object[], closed?: object[], results?: object[]}} buckets
 * @param {Date} now
 * @returns {{today: object[], future: object[], past: object[]}} each item is
 *   { event, state, from, hasResults }
 */
export const buildEventFeed = (buckets, now = new Date()) => {
  const byId = new Map();
  LISTINGS.forEach((bucket) => {
    (buckets?.[bucket] || []).forEach((event) => {
      if (!event?._id) return;
      const entry = byId.get(event._id) || { event, listings: new Set() };
      entry.listings.add(bucket);
      byId.set(event._id, entry);
    });
  });

  const today = [];
  const future = [];
  const past = [];
  byId.forEach(({ event, listings }) => {
    const hasResults = listings.has("results");
    const other = STATE_LISTINGS.find((b) => listings.has(b));
    const phase = eventPhase(event, now);
    const item = (state) => ({ event, state, from: fromForState(state), hasResults });
    // Running now (results-first on event day): a feis or Oireachtas links to
    // its results whatever its registration state. Grade exams have no public
    // results, so they keep their normal state.
    if (event.eventType !== "Grade Exam" && phase === "current") {
      today.push(item("today"));
    } else if (other && (phase === "before" || phase === "current" || !hasResults)) {
      // not over yet (or the server still lists it): its registration state,
      // even when a result is already published (a test publish)
      future.push(item(bucketState(other, event)));
    } else if (hasResults) {
      past.push(item("results"));
    }
  });

  today.sort(soonest);
  future.sort(soonest);
  past.sort(newestFirst);
  return { today, future, past };
};

// The Results tab's view of a feed (Devin 2026-10-31): an event is there once
// at least one of its results is published, nothing to do with the date. Running
// now with a result: under Happening now; any other event with a result
// (past, or a future one with a test publish): a results card, newest first.
export const resultsOnlyFeed = (feed) => ({
  today: (feed?.today || []).filter((i) => i.hasResults),
  future: [],
  past: [
    ...(feed?.future || []).filter((i) => i.hasResults).map((i) => ({ ...i, state: "results", from: fromForState("results") })),
    ...(feed?.past || []),
  ].sort(newestFirst),
});

// Event name, city or province; case- and accent-insensitive, so "montreal"
// finds "Montréal, Québec".
const fold = (v) => String(v).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export const matchesSearch = (item, search) => {
  const q = fold((search || "").trim());
  if (!q) return true;
  return [item.event?.name, item.event?.venue_city, item.event?.state]
    .filter(Boolean)
    .some((v) => fold(v).includes(q));
};

// A family's "Entered" card opens results instead of the registration from
// the feis day on (Devin 2026-09-25: nearly every event-day tap is for
// results), even while late entries are still open that day. Grade exams have
// no results.
export const entryOpensResults = (entry, now = new Date()) => {
  if (entry?.event_type !== "feis") return false;
  const d = eventDateOf(entry.result);
  return !!d && now >= formatDate(d);
};

// Page state for an event page reached without router state (a shared or
// typed link), on the same date rules as the server listings:
// upcoming until opening, open through the close day (and its late-entry
// window), closed until the event
// day, results from the event day on - the event day wins even when the
// close date runs to it (feis/Oireachtas; grade exams have no results).
export const pageStateForEvent = (event, now = new Date()) => {
  if (!event) return undefined;
  const opening = event.opening_date ? formatDate(event.opening_date) : null;
  const closing = event.closing_date ? formatDate(event.closing_date) : null;
  const eventDay = eventDateOf(event) ? formatDate(eventDateOf(event)) : null;
  if (event.eventType !== "Grade Exam" && eventDay && now >= eventDay) return "result";
  if (opening && now < opening) return "upcoming";
  if (closing) {
    const closeEnd = new Date(closing);
    closeEnd.setHours(23, 59, 59, 999);
    if (now <= closeEnd) return "open";
  }
  // closed, but still taking late entries (lateEntries.js): listed as open,
  // the page shows it late
  if (event.eventType !== "Oireachtas" && inLateWindow(event, now)) return "open";
  if (eventDay && now < eventDay) return "closed";
  return event.eventType === "Grade Exam" ? "closed" : "result";
};
